import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';

// WebSocket URL - production endpoint
const WS_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://hn3hitb23m.execute-api.us-east-1.amazonaws.com/prod';

export function useWebSocket() {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout>();
  
  const {
    setConnected,
    setConnectionId,
    handleMessage,
  } = useGameStore();

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    console.log('Connecting to WebSocket:', WS_URL);
    ws.current = new WebSocket(WS_URL);

    ws.current.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      setConnectionId(null);
      
      // Attempt reconnect after 3 seconds
      reconnectTimeout.current = setTimeout(() => {
        console.log('Attempting reconnect...');
        connect();
      }, 3000);
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('Received:', message);
        handleMessage(message);
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };
  }, [setConnected, setConnectionId, handleMessage]);

  const send = useCallback((action: string, data: object = {}) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ action, ...data });
      console.log('Sending:', message);
      ws.current.send(message);
    } else {
      console.error('WebSocket not connected');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { send, disconnect, reconnect: connect };
}
